// Validation shared by the build-time check (scripts/check-proposals.mjs), the on-screen report
// (/<code>?check=1) and the draft checklist panel. Pure functions; no React, no DOM.
import { checkProceeds, checkSummaries, enrichComps, summarizeGroups } from "../lib/aht/compute.js";

export const STATUSES = ["draft", "live", "expired", "withdrawn"];
export const HERO_MODES = ["photo", "sample"];
const COMP_STATUSES = ["Sold", "Active", "Pending", "First Look"];
const NEARBY_STATUSES = ["Sold by Adam", "Listed by Adam"];
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const isStr = (v) => typeof v === "string" && v.trim().length > 0;
const val = (x) => (x && typeof x === "object" && "value" in x ? x.value : x);

// Every { verify: true } left anywhere in the proposal, as dotted paths.
export function findVerifyFlags(obj, path = "", out = []) {
  if (!obj || typeof obj !== "object") return out;
  if (obj.verify === true) out.push(path || "(root)");
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === "object") findVerifyFlags(v, path ? `${path}.${k}` : k, out);
  }
  return out;
}

// Every image path the proposal references.
export function imagePaths(p) {
  const out = [];
  const m = p.meta || {};
  if (m.heroMode === "photo" && m.heroImage) {
    if (typeof m.heroImage === "string") out.push({ path: m.heroImage, where: "meta.heroImage" });
    else (m.heroImage.sources || []).forEach((s, i) => out.push({ path: s.src, where: `meta.heroImage.sources[${i}]` }));
  }
  (p.nearbySales || []).forEach((h, i) => h.photo && out.push({ path: h.photo, where: `nearbySales[${i}] (${h.address})`, optional: true }));
  return out;
}

/**
 * Validate one proposal.
 * opts.imageExists(path) -> Promise<boolean> (relative to /public/images)
 * opts.today -> Date, opts.expectedCode -> string (registry key / file name)
 * Returns { errors, warnings } as plain-English strings. Errors block a live proposal.
 */
export async function validateProposal(p, opts = {}) {
  const errors = [];
  const warnings = [];
  const today = opts.today || new Date();
  const err = (m) => errors.push(m);
  const warn = (m) => warnings.push(m);

  if (!p || typeof p !== "object") return { errors: ["The proposal file does not export an object."], warnings };
  const m = p.meta || {};
  const live = m.status === "live";

  // meta
  if (!isStr(m.code)) err("meta.code is missing.");
  else if (!/^[a-z0-9-]+$/.test(m.code)) err(`meta.code "${m.code}" should be lowercase letters, numbers and dashes only.`);
  if (opts.expectedCode && m.code !== opts.expectedCode) err(`meta.code "${m.code}" does not match the registry key / file name "${opts.expectedCode}".`);
  if (!isStr(val(m.preparedFor))) err("meta.preparedFor (the client's name) is missing.");
  if (!STATUSES.includes(m.status)) err(`meta.status must be one of ${STATUSES.join(", ")}.`);
  for (const k of ["createdOn", "expiresOn", "dataAsOf"]) {
    if (!isStr(m[k]) || !DATE.test(m[k]) || Number.isNaN(Date.parse(m[k]))) err(`meta.${k} must be a date written as YYYY-MM-DD.`);
  }
  if (live && DATE.test(m.expiresOn || "") && new Date(`${m.expiresOn}T23:59:59`) < today) err(`meta.expiresOn (${m.expiresOn}) is in the past; a live proposal must expire in the future.`);
  if (!HERO_MODES.includes(m.heroMode)) err(`meta.heroMode must be "photo" or "sample".`);
  if (m.heroMode === "photo" && !m.heroImage) err('meta.heroMode is "photo" but meta.heroImage is empty. Add the photo or switch to "sample".');

  // subject
  const s = p.subject || {};
  for (const k of ["address", "cityLine", "neighborhood"]) if (!isStr(s[k])) err(`subject.${k} is missing.`);
  for (const k of ["beds", "baths", "sqft", "lotSqft", "lotAcres", "yearBuilt", "garage"]) if (!isNum(s[k])) err(`subject.${k} must be a number.`);
  if (!isStr(val(s.setting))) warn("subject.setting is empty; the setting tile will have no description.");

  // pricing
  const pr = p.pricing || {};
  if (pr.showRange) {
    if (!isNum(pr.rangeLow) || !isNum(pr.rangeHigh)) err("pricing.rangeLow and pricing.rangeHigh must be numbers when pricing.showRange is true.");
    else if (pr.rangeLow >= pr.rangeHigh) err("pricing.rangeLow must be lower than pricing.rangeHigh.");
  }

  // comps
  if (!Array.isArray(p.comps) || !p.comps.length) err("comps is empty. Add the comparable homes from the Cloud CMA.");
  else {
    p.comps.forEach((c, i) => {
      const tag = `comps[${i}] (${c.address || "no address"})`;
      if (!isStr(c.address)) err(`${tag}: address is missing.`);
      if (!COMP_STATUSES.includes(c.status)) err(`${tag}: status must be one of ${COMP_STATUSES.join(", ")}.`);
      for (const k of ["price", "originalPrice", "beds", "baths", "sqft", "lotSqft", "yearBuilt"]) if (!isNum(c[k])) err(`${tag}: ${k} must be a number.`);
      if (c.status === "Sold" && !DATE.test(c.soldDate || "")) err(`${tag}: sold homes need soldDate as YYYY-MM-DD.`);
      if (c.status !== "Sold" && !isNum(c.daysOnMarket)) err(`${tag}: daysOnMarket must be a number for homes that have not sold.`);
      if (!isStr(c.listingFirm)) warn(`${tag}: listingFirm is missing (the NWMLS courtesy line will be blank).`);
    });
    if (p.compsSummaryCheck) {
      try {
        const r = checkSummaries(summarizeGroups(enrichComps(p.comps)), p.compsSummaryCheck);
        if (!r.pass) r.problems.forEach((x) => err(`Comps summary check: ${x}`));
      } catch (e) {
        err(`Comps summary check could not run: ${e.message}`);
      }
    } else warn("compsSummaryCheck is missing, so the comps averages are not being verified against the CMA.");
  }
  if (!isStr(p.compsAttribution)) err("compsAttribution (the NWMLS courtesy line with the CMA date) is missing.");

  // market
  if (!p.market || !p.seasonal) warn("market or seasonal is missing; the market section will be hidden.");
  else {
    for (const k of ["medianPrice", "monthsOfInventory", "daysOnMarket", "activeListings", "homesSold"]) {
      if (k in p.market && !isNum(p.market[k])) err(`market.${k} must be a number.`);
    }
  }

  // proceeds
  const t = p.sectionToggles || {};
  if (t.proceeds !== false && p.proceeds && p.proceeds.show !== false) {
    try {
      const r = checkProceeds(p.proceeds);
      if (!r.pass) r.problems.forEach((x) => err(`Net sheet math check: ${x}`));
    } catch (e) {
      err(`Net sheet math check could not run: ${e.message}`);
    }
  }

  // nearby sales
  const nearbyOn = t.nearbySales !== false && Array.isArray(p.nearbySales) && p.nearbySales.length > 0;
  if (nearbyOn) {
    if (!isStr(p.nearbySalesTitle)) err('nearbySalesTitle (the area label, e.g. "South of I-90") is missing.');
    p.nearbySales.forEach((h, i) => {
      const tag = `nearbySales[${i}] (${h.address || "no address"})`;
      if (!isStr(h.address)) err(`${tag}: address is missing.`);
      if (!NEARBY_STATUSES.includes(h.status)) err(`${tag}: status must be "Sold by Adam" or "Listed by Adam".`);
      for (const k of ["beds", "baths", "sqft"]) if (!isNum(h[k])) err(`${tag}: ${k} must be a number.`);
      if (!h.photo) warn(`${tag}: no photo; the card will show without one.`);
    });
    if (t.nearbyMap !== false && !isStr(p.nearbyMapMid)) err("nearbyMapMid is missing. Add the Google My Maps id or set sectionToggles.nearbyMap to false.");
  } else if (t.nearbySales !== false) warn("nearbySales is empty; the Nearby Sales section will be hidden.");
  if (p.comps && p.comps.length && t.compsMap !== false && !isStr(p.compsMapMid)) err("compsMapMid is missing. Add the Google My Maps id or set sectionToggles.compsMap to false.");

  // pricing stories
  if (!Array.isArray(p.pricingStories) || !p.pricingStories.length) warn("pricingStories is empty; the pricing-strategy section will be hidden.");

  // images
  if (opts.imageExists) {
    for (const img of imagePaths(p)) {
      const ok = await opts.imageExists(img.path);
      if (!ok) (img.optional ? warn : err)(`Image not found: images/${img.path} (${img.where}).${img.optional ? " The card will show without a photo." : ""}`);
    }
  }

  // verify flags
  const flags = findVerifyFlags(p);
  if (flags.length) (live ? err : warn)(`${flags.length} value${flags.length === 1 ? " is" : "s are"} still marked verify: true: ${flags.join(", ")}.`);

  return { errors, warnings };
}
